/* eslint-disable react/no-unused-prop-types */
import Box from '@mui/material/Box';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PropTypes from 'prop-types';
import React, {useEffect, useRef} from 'react';
import Typography from '@mui/material/Typography';
import {Virtuoso} from 'react-virtuoso';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import MosaicListItem from './mosaic-list-item.jsx';
import {
  EmptyMessageContainer,
  ErrorMessage,
  LoadingMessage,
} from '@explorer/components/ui/message-container';
import {MODALS, UI_MODES} from '@explorer/constants';
import {
  fetchSeriesMosaicsRequest,
  selectMosaic,
} from '@explorer/actions/mosaics';
import {getMosaics} from '@explorer/selectors/mosaics';
import {getSelectedSeries} from '@explorer/selectors/series';
import {showModal} from '@explorer/actions/ui';

const ListContainer = React.forwardRef((props, ref) => (
  <Box ref={ref} display="flex" flexDirection="column" flex={1} {...props} />
));

const MosaicsList = ({
  fetchSeriesMosaicsRequest,
  activeSeries,
  selectedMosaic,
  selectMosaic,
  ...props
}) => {
  const listRef = useRef();
  const {t} = useTranslation();

  useEffect(() => {
    if (activeSeries?.id) {
      fetchSeriesMosaicsRequest(activeSeries.id);
    }
  }, [activeSeries, fetchSeriesMosaicsRequest]);

  const onRequestAccess = () => {
    props.showModal(MODALS.BASEMAPS_NO_ACCESS);
  };

  const onClickMosaic = mosaic => () => {
    if (!props.isComparing && mosaic.hasAccess) {
      if (selectedMosaic && selectedMosaic.id === mosaic.id) {
        selectMosaic(null);
      } else {
        selectMosaic(mosaic.id);
      }
    }
  };

  const rowRenderer = index => {
    const mosaic = props.mosaics[index];
    const isCompareSubject = Object.values(props.compareSubjects).some(
      subject => subject && subject.id === mosaic?.id
    );

    const selected = props.isComparing
      ? isCompareSubject
      : mosaic && selectedMosaic?.id === mosaic?.id;

    return (
      <MosaicListItem
        mosaic={mosaic}
        selected={selected}
        isComparing={props.isComparing}
        onClick={onClickMosaic(mosaic)}
        onRequestAccess={onRequestAccess}
        data-qe="mosaic-list-item"
      />
    );
  };

  if (props.mosaicsError) {
    return <ErrorMessage message={t('sidebarsBasemap.errorLoadingBasemaps')} />;
  } else if (props.mosaicsLoading) {
    return <LoadingMessage type="basemaps" />;
  } else if (!props.mosaicsLoading && props.mosaics.length === 0) {
    return (
      <EmptyMessageContainer
        icon={<ErrorOutlineOutlinedIcon fontSize="inherit" color="error" />}
        data-qe="no-basemaps-message"
      >
        <Typography variant="h6" color="inherit">
          {t('sidebarsBasemap.errorNoBasemaps')}
        </Typography>
      </EmptyMessageContainer>
    );
  }

  const activeIndex = props.mosaics.findIndex(
    mosaic => mosaic.id === selectedMosaic?.id
  );

  return (
    <Virtuoso
      ref={listRef}
      overscan={500}
      totalCount={props.mosaics.length || 0}
      initialTopMostItemIndex={activeIndex >= 0 ? activeIndex : 0}
      itemContent={rowRenderer}
      components={{
        List: ListContainer,
      }}
    />
  );
};

MosaicsList.propTypes = {
  activeSeries: PropTypes.object,
  compareSubjects: PropTypes.object,
  fetchSeriesMosaicsRequest: PropTypes.func,
  isComparing: PropTypes.bool,
  mosaics: PropTypes.array,
  mosaicsError: PropTypes.bool,
  mosaicsLoading: PropTypes.bool,
  selectMosaic: PropTypes.func,
  showModal: PropTypes.func,
  selectedMosaic: PropTypes.object,
};

const mapStateToProps = state => ({
  activeSeries: getSelectedSeries(state),
  compareSubjects: state.compare.subjects,
  isComparing: state.ui.mode === UI_MODES.COMPARE,
  mosaics: getMosaics(state),
  mosaicsError: !!state.mosaics.error,
  mosaicsLoading: state.mosaics.loading,
  selectedMosaic: state.mosaics.selected,
});

const mapDispatchProps = {
  fetchSeriesMosaicsRequest,
  selectMosaic,
  showModal,
};

export default connect(mapStateToProps, mapDispatchProps)(MosaicsList);
